import size from 'lodash/size';
import isEqual from 'lodash/isEqual'
import difference from 'lodash/difference'
import { ProductTypeEnum } from '@nucleo/product';
import { PriceModeEnum, NO_TOUR } from './constants';
import './flat-polyfill';

export const AllProductSummariesHelper = {
  getVisitSummaries: props => {
    const data = props.data || props.dashboard;

    if(!data) {
      return [];
    }

    let visits = [];
    const {
      visitSummaries,
      tourSummaries
    } = data;
    if(size(visitSummaries) > 0) {
      visits = visitSummaries;
    } else if(size(tourSummaries)) {
      tourSummaries.forEach(tour => {
        const { visits: tourVisits } = tour;
        tourVisits.forEach(tourVisit => {
          visits.push(tourVisit);
        })
      })
    }
    return visits;
  },

  getEventSummaries: props => {
    const data = props.data || props.dashboard;

    if(!data) {
      return [];
    }

    let events = [];
    const {
      eventSummaries,
      tourSummaries
    } = data;
    if(size(eventSummaries) > 0) {
      events = eventSummaries;
    } else if(size(tourSummaries)) {
      tourSummaries.forEach(tour => {
        const { events: tourEvents } = tour;
        tourEvents.forEach(tourEvent => {
          events.push(tourEvent);
        })
      })
    }
    return events;
  },

  getPassSummaries: props => {
    const data = props.data || props.dashboard;

    if(!data) {
      return [];
    }

    let passes = [];
    const {
      passSummaries,
      tourSummaries
    } = data;
    if(size(passSummaries) > 0) {
      passes = passSummaries;
    } else if(size(tourSummaries)) {
      tourSummaries.forEach(tour => {
        const { passes: tourPasses } = tour;
        tourPasses.forEach(tourPasse => {
          passes.push(tourPasse);
        })
      })
    }
    return passes;
  },

  getTourSummaries: props => {
    const data = props.data || props.dashboard;

    if(!data) {
      return [];
    }

    return data.tourSummaries;
  },

  getProductSummaries: (props, productType) => {
    switch (productType) {
      case ProductTypeEnum.EVENT:
        return AllProductSummariesHelper.getEventSummaries(props);
      case ProductTypeEnum.VISIT:
        return AllProductSummariesHelper.getVisitSummaries(props);
      case ProductTypeEnum.OPEN_PASS:
        return AllProductSummariesHelper.getPassSummaries(props);
      case ProductTypeEnum.TOUR:
        return AllProductSummariesHelper.getTourSummaries(props);
       default:
        return [];
    }
  }
}

export const getEventIds = (tourId, tours) => {
  const eventIds = [];
  (tours || []).forEach(tour => {
    if(tour.tourId === tourId) {
      tour.events.forEach(event => {
        eventIds.push(event.id);
      })
    }
  });
  return eventIds;
}

export const getVisitIds = (tourId, tours) => {
  const visitIds = [];
  (tours || []).forEach(tour => {
    if(tour.tourId === tourId) {
      tour.visits.forEach(visit => {
        visitIds.push(visit.id);
      })
    }
  });
  return visitIds;
}

export const getOpenPassIds = (tourId, tours) => {
  const passIds = [];
  (tours || []).forEach(tour => {
    if(tour.tourId === tourId) {
      tour.passes.forEach(pass => {
        passIds.push(pass.id);
      })
    }
  });
  return passIds;
}

export const getTourPerformanceIds = (tourId, tours) => {
  const performanceIds = [];
  (tours || []).forEach(tour => {
    if(tourId === tour.tourId) {
      const { events } = tour;
      events.forEach(event => {
        (event.performances || []).forEach(perf => {
          performanceIds.push(perf.id);
        })
      })
    }
  });
  return performanceIds;
}

export const getTourTimeslotMonths = (tourId, tours) => {
  const monthTimeSlots = [];
  tours.forEach(tour => {
    if (tourId === tour.tourId) {
      const { visits } = tour;
      visits.forEach(visit => {
        let _monthTimeSlots = (visit.monthTimeslots || []).map(monthTimeslot =>
          [visit.id, monthTimeslot.monthKey].join('-'));
        monthTimeSlots.push(..._monthTimeSlots);
      })
    }
  });
  return monthTimeSlots;
}

export const getTourTimeslotDates = (tourId, tours) => {
  const dateTimeSlots = [];
  tours.forEach(tour => {
    if(tourId === tour.tourId) {
      const { visits } = tour;
      visits.forEach(visit => {
        let _dateTimeSlots = (visit.monthTimeslots || [])
          .map(monthTimeslot => monthTimeslot.timeslots.map(t => Object.assign(t, { monthKey: monthTimeslot.monthKey })))
          .reduce((ts1, ts2) => ts1.concat(ts2), [])
          .map(timeslot => toTimeslotDateId(visit.id, timeslot.monthKey, timeslot.date));
        dateTimeSlots.push(..._dateTimeSlots);
      })
    }
  });
  return dateTimeSlots;
}

export const getPerformanceIds = (eventId, events) => {
  const performanceIds = [];
  (events || []).forEach(event => {
    if (event.id === eventId) {
      (event.performances || []).forEach(perf => {
        performanceIds.push(perf.id);
      });
    }
  });
  return performanceIds;
};

export const toTimeslotDateId = (visitId, monthKey, timeslotDate) =>
  [visitId, monthKey, new Date(timeslotDate).getTime()].join('-');

export const getTimeslotDateIds = (visitId, visits = []) => {
  const timeslotDates = [];
  visits.forEach(visit => {
    if (visit.id === visitId) {
      (visit.monthTimeslots || []).forEach(monthTimeslot => {
        (monthTimeslot.timeslots || []).forEach(timeslot => {
          timeslotDates.push(
            toTimeslotDateId(visit.id, monthTimeslot.monthKey, timeslot.date)
          );
        });
      });
    }
  });
  return timeslotDates;
};

export const getMonthTimeslotIds = (visitId, monthKey, visits = []) =>
  (visits.find(visit => visit.id === visitId).monthTimeslots || [])
    .find(at => at.monthKey === monthKey)
    .timeslots.map(t => t.timeslotDate);

export const getTimeslotMonths = (visitId, visits = []) =>
  (visits.find(visit => visit.id === visitId).monthTimeslots || [])
    .map(monthTimeslot => [visitId, monthTimeslot.monthKey].join('-'));

export const getTimeslotDates = (visitId, visits = [], monthKey) => {
  return (visits.find(visit => visit.id === visitId).monthTimeslots || [])
    .filter(monthTimeslot => !monthKey || monthTimeslot.monthKey === monthKey)
    .map(monthTimeslot => monthTimeslot.timeslots.map(t => Object.assign(t, { monthKey: monthTimeslot.monthKey })))
    .reduce((ts1, ts2) => ts1.concat(ts2), [])
    .map(timeslot => toTimeslotDateId(visitId, timeslot.monthKey, timeslot.date));
}

export const getProductTypeFromLocalStorage = () => {
  if (!localStorage) {
    return '';
  }
  return localStorage.getItem('reportDashboardProductType');
};

export const storeProductTypeToLocalStorage = productType => {
  if (!localStorage) {
    return;
  }
  localStorage.setItem('reportDashboardProductType', productType);
};

export const mergeState = (setStateFunction, partialState, callBack) => {
  setStateFunction(prevState => Object.assign({}, prevState, partialState), callBack);
};

export const findDayRange = dates => {
  const allDates = dates.map(timeslot => timeslot.date).sort();
  return [allDates[0], allDates.reverse()[0]];
};
/*
* This helper using for handle update unselected data when dashboard get new data (load more / searching / ...)
 */
export const updateUnselectedItemHelper = {
  getUnselectedTimeslotDates: (preProps, props, state, forceUnselected) => {
    const currentUnselectedDates = state.unselectedTimeslotDates;
    const oldVisitSummaries = AllProductSummariesHelper.getVisitSummaries(preProps);
    const newVisitSummaries = AllProductSummariesHelper.getVisitSummaries(props);
    const oldDateIds = oldVisitSummaries
      .map(visit => visit.monthTimeslots.map(month => month.timeslots.map(timeslot => timeslot.id)).flat())
      .flat();
    const newDateIds = newVisitSummaries
      .map(visit => visit.monthTimeslots.map(month => month.timeslots.map(timeslot => timeslot.id)).flat())
      .flat();
    const newUnselectedDateIds = [];
    if (forceUnselected || size(oldVisitSummaries) === 0) {
      newUnselectedDateIds.push(...newDateIds);
    } else if (size(currentUnselectedDates) > 0 || !isEqual(oldVisitSummaries, newVisitSummaries)) {
      const newDates = newDateIds.filter(newDateId => oldDateIds.indexOf(newDateId) === -1);
      newUnselectedDateIds.push(...newDates);
      // Filter current unselected date ids that not contain in new date ids
      const filteredUnselectedDates = currentUnselectedDates.filter(dateId => newDateIds.indexOf(dateId) !== -1);
      newUnselectedDateIds.push(...filteredUnselectedDates);
    } else {
      newUnselectedDateIds.push(...currentUnselectedDates)
    }

    return newUnselectedDateIds;
  },

  getUnselectedTimeslotMonths: (preProps, props, state, newUnselectedDateIds, forceUnselected) => {
    const currentUnselectedMonths = state.unselectedTimeslotMonths;
    const oldVisitSummaries = AllProductSummariesHelper.getVisitSummaries(preProps);
    const newVisitSummaries = AllProductSummariesHelper.getVisitSummaries(props);
    const oldMonthSummaries = oldVisitSummaries.map(visit => visit.monthTimeslots).flat();
    const newMonthSummaries = newVisitSummaries.map(visit => visit.monthTimeslots).flat();
    const newUnselectedMonthIds = [];

    if (forceUnselected || size(oldMonthSummaries) === 0) {
      newUnselectedMonthIds.push(...newMonthSummaries.map(month => month.id));
    } else if (size(currentUnselectedMonths) > 0 || !isEqual(oldMonthSummaries, newMonthSummaries)) {
      const newMonths = newMonthSummaries.filter(newMonth => !oldMonthSummaries.find(oldMonth => oldMonth.id === newMonth.id));
      newUnselectedMonthIds.push(...newMonths.map(month => month.id));

      currentUnselectedMonths.forEach(monthId => {
        const month = newMonthSummaries.find(monthData => monthData.id === monthId);
        if (!month) {
          return;
        }
        const newUnselectedTimeslotDates = month.timeslots
          .map(timeslot => timeslot.id)
          .filter(timeslotId => newUnselectedDateIds.indexOf(timeslotId) !== -1);
        if (size(newUnselectedTimeslotDates) > 0) {
          newUnselectedMonthIds.push(monthId);
        }
      });
    } else {
      newUnselectedMonthIds.push(...currentUnselectedMonths);
    }
    return newUnselectedMonthIds;
  },

  getUnselectedVisitIds: (preProps, props, state, newUnselectedMonthIds, newUnselectedDateIds, forceUnselected) => {
    const currentUnselectedVisitIds = state.unselectedVisitIds;
    const oldVisits = AllProductSummariesHelper.getVisitSummaries(preProps);
    const newVisits = AllProductSummariesHelper.getVisitSummaries(props);
    const newUnselectedVisitIds = [];
    if (forceUnselected || size(oldVisits) === 0) {
      newUnselectedVisitIds.push(...newVisits.map(visit => visit.id));
    } else if (size(currentUnselectedVisitIds) > 0 || !isEqual(oldVisits, newVisits)) {
      const changedVisits = newVisits.filter(newVisit => !oldVisits.find(oldVisit => oldVisit.id === newVisit.id));
      newUnselectedVisitIds.push(...changedVisits.map(visit => visit.id));

      currentUnselectedVisitIds.forEach(visitId => {
        const visit = newVisits.find(visitData => visitData.id === visitId);
        if (!visit) {
          return;
        }
        const unselectedMonthIdsOfVisit = visit.monthTimeslots
          .map(month => month.id)
          .filter(monthId => newUnselectedMonthIds.indexOf(monthId) !== -1);
        const unselectedTimeslotDateIdsVisit = visit.monthTimeslots
          .map(month => month.timeslots.map(timeslot => timeslot.id))
          .flat()
          .filter(timeslotDateId => newUnselectedDateIds.indexOf(timeslotDateId) !== -1);

        if (size(unselectedMonthIdsOfVisit) > 0 || size(unselectedTimeslotDateIdsVisit) > 0) {
          newUnselectedVisitIds.push(visitId);
        }
      });
    } else {
      newUnselectedVisitIds.push(...currentUnselectedVisitIds)
    }
    return newUnselectedVisitIds;
  },

  getUnselectedPerformanceIds: (preProps, props, state, forceUnselected) => {
    const currentUnselectedPerfIds = state.unselectedPerformanceIds;
    const oldPerformanceIds = AllProductSummariesHelper.getEventSummaries(preProps)
      .map(event => event.performances.map(perf => perf.id)).flat();
    const newPerformanceIds = AllProductSummariesHelper.getEventSummaries(props)
      .map(event => event.performances.map(perf => perf.id)).flat();

    const newUnselectedPerfIds = [];
    if (forceUnselected || size(oldPerformanceIds) === 0) {
      newUnselectedPerfIds.push(...newPerformanceIds);
      return newUnselectedPerfIds;
    } else if (size(currentUnselectedPerfIds) > 0 || !isEqual(oldPerformanceIds, newPerformanceIds)) {
      const changePerformanceIds = newPerformanceIds.filter(perfId => oldPerformanceIds.indexOf(perfId) === -1);
      newUnselectedPerfIds.push(...changePerformanceIds);
      const filteredUnselectedPerfIds = currentUnselectedPerfIds.filter(
        performanceId => newPerformanceIds.indexOf(performanceId) !== -1
      );
      newUnselectedPerfIds.push(...filteredUnselectedPerfIds);
    } else {
      newUnselectedPerfIds.push(...currentUnselectedPerfIds);
    }
    return newUnselectedPerfIds;
  },

  getUnselectedEventIds: (preProps, props, state, newUnselectedPerfIds, forceUnselected) => {
    const currentUnselectedEventIds = state.unselectedEventIds;
    const eventSummaries = AllProductSummariesHelper.getEventSummaries(props);
    const preEventSummaries = AllProductSummariesHelper.getEventSummaries(preProps);
    const newUnselectedEventIds = [];

    if (forceUnselected || size(preEventSummaries) === 0) {
      newUnselectedEventIds.push(...eventSummaries.map(event => event.id));
    } else if (size(currentUnselectedEventIds) > 0 || !isEqual(eventSummaries, preEventSummaries)) {
      const newEvents = eventSummaries.filter(
        event => !preEventSummaries.find(preEvent => preEvent.id === event.id)
      );
      newUnselectedEventIds.push(...newEvents.map(event => event.id));

      currentUnselectedEventIds.forEach(eventId => {
        const event = eventSummaries.find(eventData => eventData.id === eventId);
        if (!event) {
          return;
        }
        const unselectedPerfIdsOfEvent = event.performances
          .map(perf => perf.id)
          .filter(perfId => newUnselectedPerfIds.indexOf(perfId) !== -1);

        if (size(unselectedPerfIdsOfEvent) > 0) {
          newUnselectedEventIds.push(eventId);
        }
      });
    } else {
      newUnselectedEventIds.push(...currentUnselectedEventIds);
    }
    return newUnselectedEventIds;
  },

  getUnselectedAllProductIds: (prevProps, props, state, forceUnselected) => {
    const {
      unselectedTourIds: currentUnselectedTourIds,
      unselectedEventIds: currentUnselectedEventIds,
      unselectedVisitIds: currentUnselectedVisitIds,
      unselectedPassIds: currentUnselectedPassIds,
      unselectedPerformanceIds: currentUnselectedPerformanceIds,
      unselectedTimeslotMonths: currentUnselectedTimeslotMonths,
      unselectedTimeslotDates: currentUnselectedTimeslotDates,
    } = state;
    const tourSummaries = AllProductSummariesHelper.getTourSummaries(props);
    const preTourSummaries = AllProductSummariesHelper.getTourSummaries(prevProps);
    const newUnselectedTourIds = [];
    const newUnselectedEventsIds = [];
    const newUnselectedVisitIds = [];
    const newUnselectedPassIds = [];
    const newUnselectedPerformanceIds = [];
    const newUnselectedTimeslotMonths = [];
    const newUnselectedTimeslotDates = [];

    if (forceUnselected || size(preTourSummaries) === 0) {
      newUnselectedTourIds.push(...tourSummaries.map(tour => tour.tourId));
    } else {
      const newTourIds = difference(tourSummaries.map(tour => tour.tourId), preTourSummaries.map(tour => tour.tourId));
      newUnselectedTourIds.push(...currentUnselectedTourIds, ...newTourIds);
    }

    if (size(newUnselectedTourIds) > 0) {
      newUnselectedTourIds.forEach(tourId => {
        const tour = tourSummaries.find(t => t.tourId === tourId);
        if (!tour) {
          return;
        }
        const { events, visits, passes } = tour;
        events.forEach(event => {
          const { performances } = event;
          newUnselectedEventsIds.push(event.id);
          performances.forEach(performance => newUnselectedPerformanceIds.push(performance.id));
        });

        visits.forEach(visit => {
          const { monthTimeslots } = visit;
          newUnselectedVisitIds.push(visit.id);
          monthTimeslots.forEach(monthTimeslot => {
            const { timeslots } = monthTimeslot;
            newUnselectedTimeslotMonths.push(monthTimeslot.id);
            timeslots.forEach(timeslot => newUnselectedTimeslotDates.push(timeslot.id));
          })
        });

        passes.forEach(pass => newUnselectedPassIds.push(pass.id));
      });
    }

    return {
      unselectedTourIds: [...newUnselectedTourIds],
      unselectedEventIds: [...currentUnselectedEventIds, ...newUnselectedEventsIds],
      unselectedVisitIds: [...currentUnselectedVisitIds, ...newUnselectedVisitIds],
      unselectedPassIds: [...currentUnselectedPassIds, ...newUnselectedPassIds],
      unselectedPerformanceIds: [...currentUnselectedPerformanceIds, ...newUnselectedPerformanceIds],
      unselectedTimeslotMonths: [...currentUnselectedTimeslotMonths, ...newUnselectedTimeslotMonths],
      unselectedTimeslotDates: [...currentUnselectedTimeslotDates, ...newUnselectedTimeslotDates]
    }
  },

  getUnselectedPassIds: (prevProps, props, state, forceUnselected) => {
    const currentUnselectedPassIds = state.unselectedPassIds;
    const passSummaries = AllProductSummariesHelper.getPassSummaries(props);
    const prevPassSummaries = AllProductSummariesHelper.getPassSummaries(prevProps);
    const newUnselectedPassIds = [];

    if (forceUnselected || size(prevPassSummaries) === 0) {
      newUnselectedPassIds.push(...passSummaries.map(newPass => newPass.id));
    } else if (size(currentUnselectedPassIds) > 0 || !isEqual(passSummaries, prevPassSummaries)) {
      const newPasses = passSummaries.filter(pass => !prevPassSummaries.find(prevPass => prevPass.id === pass.id));
      newUnselectedPassIds.push(...newPasses.map(newPass => newPass.id));

      currentUnselectedPassIds.forEach(passId => {
        const pass = passSummaries.find(p => p.id === passId)
        if (!pass) {
          return;
        }
        newUnselectedPassIds.push(pass.id);
      })
    }
    return newUnselectedPassIds;
  }
};

export const getRevenueData = (revenueData, priceMode) =>
  priceMode === PriceModeEnum.FULL_PRICE ? revenueData.revenue : revenueData.ticketPrice;

export const getPotentialRevenueData = (revenueData, priceMode) =>
  priceMode === PriceModeEnum.FULL_PRICE
    ? revenueData.potentialRevenue
    : revenueData.potentialTicketPrice;


const ENGLISH_LANG_CODE = "en";

// eslint-disable-next-line func-names
export const TranslationModule = (function(){
  let lang = ENGLISH_LANG_CODE;
  let defaultLang = ENGLISH_LANG_CODE;
  let useInternalName = false;
  let eventSummaries = [];
  let visitSummaries = [];
  let passSummaries = [];
  let tourSummaries = [];

  const setLang = language =>  {
    if(!language) return -1;
    lang = language;
    return 1;
  };

  const setDefaultLang = language => {
    if(!language) return -1;
    defaultLang = language;
    return 1;
  };

  const getTranslatedName = (name, langCode, defaultLangCode) => {
    if (!name) {
      return '';
    }
    if (!langCode) {
      return name;
    }
    if (Object.keys(name).length === 1) {
      return name[Object.keys(name)[0]];
    }
    if (!name[langCode]) {
      if (!defaultLangCode || !name[defaultLangCode]) {
        return name[Object.keys(name)[0]];
      }
      return name[defaultLangCode];
    }
    return name[langCode];
  }

  const getTranslate = name => getTranslatedName(name, lang, defaultLang);

  const translateEvent = event => ({
    ...event,
    name: getTranslate(useInternalName ? event.internalName : event.name),
    tour: getTranslate(event.tour),
    site: getTranslate(event.site),
    performances: event.performances.map(perf => ({
      ...perf,
      site: getTranslate(perf.site),
      space: getTranslate(perf.space),
      teamA: getTranslate(perf.teamA),
      teamB: getTranslate(perf.teamB),
      matchDay: getTranslate(perf.matchDay),
    }))
  });

  const translateVisit = visit => ({
    ...visit,
      name: getTranslate(useInternalName ? visit.internalName : visit.name),
      tour: getTranslate(visit.tour),
      site: getTranslate(visit.site),
  });

  const translatePass = pass => {
    const sites = {};
    Object.keys(pass.siteNameByZipCode).map(key => {
      sites[key] = getTranslate(pass.siteNameByZipCode[key]);
    })
    return ({
      ...pass,
      name: getTranslate(useInternalName ? pass.internalName : pass.name),
      tour: getTranslate(pass.tour),
      seasonName: getTranslate(pass.seasonNames),
      sites,
    });
  };

  const translateEventSummaries = () => {;
    return (eventSummaries || []).map(event => translateEvent(event));
  };

  const translateVisitSummaries = () => {;
    return (visitSummaries || []).map(visit => translateVisit(visit));
  };

  const translatePassSummaries = () => {;
    return (passSummaries || []).map(pass => translatePass(pass));
  };

  const translateTourSummaries = () => {;
    return (tourSummaries || []).map(tour => {
      let { events, visits, passes } = tour;
      const { name, internalName } = tour;
      let tourName = "";
      if(useInternalName) {
        tourName = internalName && getTranslate(internalName);
      } else {
        tourName = name && getTranslate(name);
      }
      events = events.map(event => translateEvent(event, getTranslate, useInternalName));
      visits = visits.map(visit => translateVisit(visit, getTranslate, useInternalName));
      passes = passes.map(pass => translatePass(pass, getTranslate, useInternalName));
      return {
        ...tour,
        name: tourName || NO_TOUR,
        events,
        visits,
        passes
      }
    })
  };

  const init = (data, configState) => {
    eventSummaries = data.eventSummaries || [];
    visitSummaries = data.visitSummaries || [];
    passSummaries = data.passSummaries || [];
    tourSummaries = data.tourSummaries || [];
    setLang(configState.language);
    setDefaultLang(configState.defaultLanguage);
    useInternalName = configState.producerDisplayConfig && configState.producerDisplayConfig.useInternalName;
  }

  const translateDashboardData = (data, configState) => {
    if(!data || !configState ) {
      return null;
    }

    init(data, configState);

    return {
      ...data,
      eventSummaries: translateEventSummaries(),
      visitSummaries: translateVisitSummaries(),
      passSummaries: translatePassSummaries(),
      tourSummaries: translateTourSummaries()
    }
  };

  return {
    translateDashboardData,
    getTranslatedName
  }
})();


export const round = (number, scales) => {
  // eslint-disable-next-line prefer-template
  return +(Math.round(number + "e+" + scales)  + "e-" + scales);
}
